import { Icon } from "@/react/components/shared/Icon";

import type { MostAttendedEvent } from "@circle-react/apps/EoyRecapApp/types";
import { dateComponents } from "@/react/helpers/dateTimeHelpers/dateComponents";
import { formatDateString } from "@/react/helpers/dateTimeHelpers/getLocalizedDate";
import { t } from "@/i18n-js/instance";

interface MostPopularEventProps {
  event: MostAttendedEvent;
}

export const MostPopularEvent = ({ event }: MostPopularEventProps) => {
  if (!event) return null;

  const { dayOfMonth, shortMonthString } = dateComponents(event.date);
  const monthDay = formatDateString(event.date, {
    month: "short",
    day: "numeric",
  });
  const dateTimeDisplay = monthDay;
  const remainingAttendees = Math.max(0, event.rsvps - 3);

  return (
    <div className="relative w-full max-w-md pb-4">
      <div className="absolute inset-0 bg-[#3A1F60] rounded-2xl translate-y-14 scale-[0.80]" />
      <div className="absolute inset-0 bg-[#583984] rounded-2xl translate-y-6 scale-[0.9]" />

      <div className="relative bg-[#3A276E] rounded-2xl p-6 shadow-2xl z-10 flex flex-col gap-5">
        <div className="flex items-start gap-4 mb-1">
          <div className="bg-[#583984] flex p-1 px-3 shrink-0 flex-col items-center justify-center rounded-lg">
            <div className="text-white text-center text-base font-bold">
              {dayOfMonth}
            </div>
            <div className="text-white text-center text-xs font-medium uppercase">
              {shortMonthString}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-white text-base font-semibold leading-tight text-balance line-clamp-3 mb-2">
              {event.name}
            </h3>

            <div className="flex items-center gap-2 text-white">
              <Icon
                type="24-calendar-number"
                className="!size-4 text-gray-300"
              />
              <p className="text-sm text-gray-300">{dateTimeDisplay}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className="flex -space-x-2">
              {[0, 1, 2].slice(0, Math.min(3, event.rsvps)).map(position => (
                <div
                  key={`avatar-${event.name}-pos-${position}`}
                  className="bg-[#3A1F60] size-8 rounded-full border-2 border-[#49335A]"
                />
              ))}
            </div>
            {remainingAttendees > 0 && (
              <span className="text-white text-sm font-medium">
                +{remainingAttendees}
              </span>
            )}
          </div>

          <button
            type="button"
            className="bg-[#583984] hover:bg-[#3A276E] text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
          >
            {t("events.rsvp.rsvp")}
          </button>
        </div>
      </div>
    </div>
  );
};
